import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { CloudArrowUp, Warning, SpinnerGap } from "@phosphor-icons/react";
import { Images } from "@/components/Images";
import { SamOverlay } from "@/components/SamOverlay";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { processImages, initializeModel, getModelInfo } from "../lib/process";
import { DEFAULT_MODEL_ID, getAvailableModels, getModelConfig } from "../lib/models";
import { resetSamState } from "../lib/sam";

interface AppError {
  message: string;
}

export interface ImageFile {
  id: number;
  file: File;
  processedFile?: File;
}

const sampleImages = [
  "https://images.unsplash.com/photo-1601233749202-95d04d5b3c00?q=80&w=2938&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1513013156887-d2bf241c8c82?q=80&w=2970&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1643490745745-e8ca9a3a1c90?q=80&w=2874&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1574158622682-e40e69881006?q=80&w=2333&auto=format&fit=crop&ixlib=rb-4.0.3",
];

const isMobileSafari = () => {
  const ua = window.navigator.userAgent;
  const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
  const webkit = !!ua.match(/WebKit/i);
  const iOSSafari =
    iOS &&
    webkit &&
    !ua.match(/CriOS/i) &&
    !ua.match(/OPiOS/i) &&
    !ua.match(/FxiOS/i);
  return iOSSafari && "ontouchend" in document;
};

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);
  const [isWebGPU, setIsWebGPU] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [currentModel, setCurrentModel] = useState(DEFAULT_MODEL_ID);
  const [isModelSwitching, setIsModelSwitching] = useState(false);
  const [images, setImages] = useState<ImageFile[]>([]);
  const [samPendingImage, setSamPendingImage] = useState<{
    id: number;
    file: File;
  } | null>(null);

  useEffect(() => {
    if (isMobileSafari()) {
      window.location.href = "https://bg-mobile.addy.ie";
      return;
    }

    const { isIOS: isIOSDevice, isWebGPUSupported } = getModelInfo();
    setIsIOS(isIOSDevice);
    setIsWebGPU(isWebGPUSupported);
    setIsLoading(false);
  }, []);

  const handleModelChange = async (newModel: string) => {
    setIsModelSwitching(true);
    setError(null);

    // Clean up if switching away from SAM
    const oldConfig = getModelConfig(currentModel);
    if (oldConfig?.interactive) {
      resetSamState();
      setSamPendingImage(null);
    }

    const newConfig = getModelConfig(newModel);
    if (newConfig?.interactive) {
      // Interactive models handle their own initialization — just set the model ID
      setCurrentModel(newModel);
      setIsModelSwitching(false);
      return;
    }

    // Auto-segmentation models: use existing initializeModel pipeline
    try {
      const initialized = await initializeModel(newModel);
      if (!initialized) {
        throw new Error("Failed to initialize new model");
      }
      setCurrentModel(newModel);
    } catch (err) {
      setError({
        message:
          err instanceof Error ? err.message : "Failed to switch models",
      });
    } finally {
      const { currentModelId } = getModelInfo();
      setCurrentModel(currentModelId);
      setIsModelSwitching(false);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const isInteractive = getModelConfig(currentModel)?.interactive === true;

      const newImages = acceptedFiles.map((file, index) => ({
        id: Date.now() + index,
        file,
        processedFile: undefined,
      }));
      setImages((prev) => [...prev, ...newImages]);

      // Interactive SAM flow: open overlay for first image only
      if (isInteractive) {
        setSamPendingImage({ id: newImages[0].id, file: newImages[0].file });
        return;
      }

      // Auto-segmentation flow (unchanged)
      if (images.length === 0) {
        setIsLoading(true);
        setError(null);
        try {
          const initialized = await initializeModel();
          if (!initialized) {
            throw new Error("Failed to initialize background removal model");
          }
          const { isWebGPUSupported } = getModelInfo();
          setIsWebGPU(isWebGPUSupported);
        } catch (err) {
          setError({
            message:
              err instanceof Error
                ? err.message
                : "An unknown error occurred",
          });
          setImages([]);
          setIsLoading(false);
          return;
        }
        setIsLoading(false);
      }

      for (const image of newImages) {
        try {
          const result = await processImages([image.file]);
          if (result && result.length > 0) {
            setImages((prev) =>
              prev.map((img) =>
                img.id === image.id
                  ? { ...img, processedFile: result[0] }
                  : img
              )
            );
          }
        } catch (error) {
          console.error("Error processing image:", error);
        }
      }
    },
    [images.length, currentModel]
  );

  const handlePaste = async (event: React.ClipboardEvent) => {
    const clipboardItems = event.clipboardData.items;
    const imageFiles: File[] = [];
    for (const item of clipboardItems) {
      if (item.type.startsWith("image")) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }
    if (imageFiles.length > 0) {
      onDrop(imageFiles);
    }
  };

  const handleSampleImageClick = async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const file = new File([blob], "sample-image.jpg", {
        type: "image/jpeg",
      });
      onDrop([file]);
    } catch (error) {
      console.error("Error loading sample image:", error);
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".mp4"],
    },
  });

  const isBusy = isLoading || isModelSwitching;

  return (
    <div className="min-h-screen bg-background" onPaste={handlePaste}>
      <nav className="border-b bg-card">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-foreground">BG</h1>
            {!isIOS && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Model:</span>
                <Select
                  value={currentModel}
                  onValueChange={handleModelChange}
                  disabled={isBusy}
                >
                  <SelectTrigger className="w-[280px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableModels(isWebGPU, isIOS).map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.label} ({model.description})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {isIOS && (
            <p className="mt-2 text-sm text-muted-foreground">
              Using optimized iOS background removal
            </p>
          )}
          {getModelConfig(currentModel)?.interactive && (
            <p className="mt-2 text-sm text-muted-foreground">
              Drop an image, then click to select what to keep
            </p>
          )}
        </div>
      </nav>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div
          className={`grid ${images.length === 0 ? "grid-cols-2 gap-8" : "grid-cols-1"}`}
        >
          {images.length === 0 && (
            <div className="flex flex-col items-start justify-center">
              <img
                src="hero.png"
                alt="Surprised man"
                className="mb-6 h-[400px] w-full object-cover"
              />
              <h2 className="mb-4 text-3xl font-bold text-foreground">
                Remove Image Background
              </h2>
              <p className="mb-4 text-lg text-muted-foreground">
                100% Automatically and Free
              </p>
              <p className="text-muted-foreground">
                Upload your image and let our AI remove the background instantly.
                Perfect for professional photos, product images, and more.
              </p>
              <p className="mt-4 text-sm text-muted-foreground/50">
                Built with love by Addy Osmani using Transformers.js
              </p>
            </div>
          )}

          <div className={images.length === 0 ? "" : "w-full"}>
            <div
              {...getRootProps()}
              className={`mb-8 cursor-pointer rounded-lg border-2 border-dashed bg-card p-8 text-center transition-colors duration-300 ease-in-out
                ${isDragAccept ? "border-green-500 bg-green-50" : ""}
                ${isDragReject ? "border-destructive bg-red-50" : ""}
                ${isDragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary hover:bg-primary/5"}
                ${isBusy ? "cursor-not-allowed" : ""}
              `}
            >
              <input
                {...getInputProps()}
                className="hidden"
                disabled={isBusy}
              />
              <div className="flex flex-col items-center gap-2">
                {isBusy ? (
                  <>
                    <SpinnerGap className="size-12 animate-spin text-primary" />
                    <p className="text-lg text-muted-foreground">
                      {isModelSwitching
                        ? "Switching models..."
                        : "Loading background removal model..."}
                    </p>
                  </>
                ) : error ? (
                  <>
                    <Warning
                      className="size-12 text-destructive"
                      weight="fill"
                    />
                    <p className="mb-2 text-lg font-medium text-destructive">
                      {error.message}
                    </p>
                    {currentModel !== DEFAULT_MODEL_ID && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleModelChange(DEFAULT_MODEL_ID);
                        }}
                      >
                        Switch to Cross-browser Version
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <CloudArrowUp className="size-12 text-muted-foreground" />
                    <p className="text-lg text-muted-foreground">
                      {isDragActive
                        ? "Drop the images here..."
                        : "Drag and drop images here"}
                    </p>
                    <p className="text-sm text-muted-foreground/70">
                      or click to select files
                    </p>
                  </>
                )}
              </div>
            </div>

            {images.length === 0 && (
              <div className="rounded-xl border bg-card p-6 shadow-sm">
                <h3 className="mb-4 text-xl font-semibold text-foreground">
                  No image? Try one of these:
                </h3>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {sampleImages.map((url, index) => (
                    <button
                      key={index}
                      onClick={() => handleSampleImageClick(url)}
                      className="relative aspect-square overflow-hidden rounded-lg transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <img
                        src={url}
                        alt={`Sample ${index + 1}`}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  All images are processed locally on your device and are not
                  uploaded to any server.
                </p>
              </div>
            )}

            <Images
              images={images}
              onDelete={(id) =>
                setImages((prev) => prev.filter((img) => img.id !== id))
              }
            />
          </div>
        </div>
      </main>

      {samPendingImage && (
        <SamOverlay
          imageFile={samPendingImage.file}
          onAccept={(processedFile) => {
            setImages((prev) =>
              prev.map((img) =>
                img.id === samPendingImage.id
                  ? { ...img, processedFile }
                  : img
              )
            );
            setSamPendingImage(null);
          }}
          onCancel={() => {
            setImages((prev) =>
              prev.filter((img) => img.id !== samPendingImage.id)
            );
            setSamPendingImage(null);
          }}
        />
      )}
    </div>
  );
}
